import React, { useEffect, useContext, useMemo, useCallback, useState } from "react";
import {
	Alert,
	Box,
	Button,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Grid,
	Modal,
	Radio,
	TextField,
	Typography,
	Switch,
	Select,
	MenuItem,
	CircularProgress,
	AlertTitle,
} from "@mui/material";
import { uploadFileSimulation, auditPost, obtenerDatos } from "../../api/config";
import { FormNameDesc } from "./FormNameDesc";
import Swal from "sweetalert2";
import { GlobalContext } from "../../GlobalContext";
import { useTranslation } from "react-i18next";

const ModalFechaUpload = ({ open, onClose, selectedIdArea }) => {
	const { t } = useTranslation();
	const { getModuleId, site_id } = useContext(GlobalContext);

	const [fechaFin, setFechaFin] = useState("");
	const [fechaInicio, setFechaInicio] = useState("");
	const [fechaError, setFechaError] = useState("");
	const [checkedError, setCheckedError] = useState("");
	const [checked, setChecked] = useState("");

	const [selectedFile, setSelectedFile] = useState(null);

	const [formValues, setFormValues] = useState({ name: "", description: "" });
	const [formErrors, setFormErrors] = useState({ name: false, description: false });
	const [emailNotifications, setEmailNotifications] = useState(false);
	const [showAlert, setShowAlert] = useState(false);

	const [forecastOptions, setForecastOptions] = useState([]);
	const [selectedForecast, setSelectedForecast] = useState("");
	const [showForecastError, setShowForecastError] = useState(false);
	const [isLoadingForecast, setIsLoadingForecast] = useState(false);
	const [noFilesForOrigin, setNoFilesForOrigin] = useState(false);
	const [hideInfoAlert, setHideInfoAlert] = useState(false);
	const [allowRunWithoutForecast, setAllowRunWithoutForecast] = useState(false);

	const handleFormChange = (name, value) => {
		setFormValues((p) => ({ ...p, [name]: value }));
		setFormErrors((p) => ({ ...p, [name]: false }));
	};

	// Normaliza a YYYY-MM
	const normYm = (v) => {
		if (!v) return null;
		const s = String(v).trim();
		const mIso = s.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?(?:[T\s].*)?$/);
		if (mIso) {
			const y = mIso[1];
			const mm = String(Number(mIso[2])).padStart(2, "0");
			return `${y}-${mm}`;
		}
		const m = s.match(/^(\d{4})[-/]?(\d{1,2})$/);
		if (m) {
			const y = m[1];
			const mm = String(Number(m[2])).padStart(2, "0");
			return `${y}-${mm}`;
		}
		return null;
	};

	// Cargar opciones por origen
	useEffect(() => {
		setAllowRunWithoutForecast(false); // reset de decisión al cambiar origen
		if (checked === "DB" || checked === "KRONOS") {
			let cancelled = false;

			setFechaInicio("");
			setFechaFin("");
			setSelectedForecast("");

			const fetchForecastOptions = async () => {
				setIsLoadingForecast(true);
				try {
					const data = await obtenerDatos(
						`/forecast-files/forecast_by_user?site_id=${site_id}&forecast_origin=${checked}`
					);

					const list = Array.isArray(data)
						? data
						: Array.isArray(data?.forecast)
						? data.forecast
						: Object.values(data?.forecast ?? {});

					const options = list.map((it) => {
						const normalizeOrigin = (raw) => {
							const s = String(raw ?? "").toUpperCase();
							if (/KRONOS|UKG/.test(s)) return "KRONOS";
							if (/^DB$|DATABASE|BASE\s*DE\s*DATOS|BBDD/.test(s)) return "DB";
							return s || "";
						};

						const start = normYm(it.mes_inicio);
						const end = normYm(it.mes_fin);

						const displayStart = normYm(it.mes_inicio) ?? start;
						const displayEnd = normYm(it.mes_fin) ?? end;

						return {
							label: it.nombre ?? it.name ?? it.file_name ?? String(it.id ?? it.file_id ?? it.value),
							value: it.id ?? it.file_id ?? it.value ?? it.forecast_id ?? it.forecast,
							start,
							end,
							displayStart,
							displayEnd,
							origen: normalizeOrigin(it.origen),
						};
					});

					const onlySameOrigin = options.filter((o) => o.origen === checked);

					setForecastOptions(onlySameOrigin);
					setNoFilesForOrigin(onlySameOrigin.length === 0);
					setShowForecastError(false);
				} catch (error) {
					console.error("Error al obtener forecast:", error);
					if (!cancelled) {
						setForecastOptions([]);
						setNoFilesForOrigin(true);
						setShowForecastError(false);
					}
				} finally {
					if (!cancelled) {
						setIsLoadingForecast(false);
						setSelectedForecast("");
					}
				}
			};

			fetchForecastOptions();
			return () => {
				cancelled = true;
			};
		} else {
			setSelectedForecast("");
			setForecastOptions([]);
			setShowForecastError(false);
			setFechaInicio("");
			setFechaFin("");
			setNoFilesForOrigin(false);
		}
	}, [checked, site_id]);

	// Fechas válidas
	const areDatesValid = useMemo(() => {
		if (!(fechaInicio && fechaFin)) return false;
		const [yi, mi] = fechaInicio.split("-");
		const [yf, mf] = fechaFin.split("-");
		const iniTotal = Number(yi) * 12 + Number(mi);
		const finTotal = Number(yf) * 12 + Number(mf);
		return iniTotal <= finTotal;
	}, [fechaInicio, fechaFin]);

	// Fechas habilitadas con solo elegir origen (aunque no existan opciones)
	const canEditDates = !!checked && !isLoadingForecast;
	const requiresForecast = checked === "DB" || checked === "KRONOS";

	// YYYY-MM -> entero (meses acumulados)
	const toYm = useCallback((ym) => {
		if (!ym) return null;
		const [y, m] = ym.split("-");
		return Number(y) * 12 + Number(m);
	}, []);

	const doesForecastCoverSelectedRange = useCallback(
		(opt) => {
			if (!fechaInicio || !fechaFin) return true;
			const selStart = toYm(fechaInicio);
			const selEnd = toYm(fechaFin);
			const fStart = toYm(opt.start);
			const fEnd = toYm(opt.end);
			if (fStart == null || fEnd == null) return false;
			return fStart <= selStart && fEnd >= selEnd;
		},
		[fechaInicio, fechaFin, toYm]
	);

	const filteredOptions = useMemo(
		() => (forecastOptions || []).filter(doesForecastCoverSelectedRange),
		[forecastOptions, doesForecastCoverSelectedRange]
	);

	// Si cambian fechas, invalidar selección incompatible
	useEffect(() => {
		if (!selectedForecast) return;
		const found = (forecastOptions || []).find((o) => o.value === selectedForecast);
		if (!found || !doesForecastCoverSelectedRange(found)) setSelectedForecast("");
	}, [forecastOptions, selectedForecast, doesForecastCoverSelectedRange]);

	// ——— Alerta SIEMPRE visible con UKG/DB, pero con mensaje dinámico ———
	const hasAnyOptions = (forecastOptions?.length ?? 0) > 0;
	const needDates = !!checked && !isLoadingForecast && !areDatesValid;
	const filteredEmpty = areDatesValid && hasAnyOptions && filteredOptions.length === 0;
	const shouldShowInfoAlert = requiresForecast && !isLoadingForecast && !!checked;

	const originReadable = useMemo(() => {
		if (checked === "KRONOS") return "UKG";
		if (checked === "DB") return "la base de datos (DB)";
		return "el origen seleccionado";
	}, [checked]);

	const infoMessageKey = useMemo(() => {
		if (!shouldShowInfoAlert) return null;
		if (needDates) return "aux.messages.pick_dates_or_run_from_origin";
		if (areDatesValid && !hasAnyOptions && !isLoadingForecast)
			return "aux.messages.no_files_for_origin_confirm";
		if (filteredEmpty) return "aux.messages.no_files_for_dates_confirm";
		return "aux.messages.you_can_select_or_run_from_origin";
	}, [
		shouldShowInfoAlert,
		needDates,
		areDatesValid,
		hasAnyOptions,
		isLoadingForecast,
		filteredEmpty,
	]);
	useEffect(() => {
		setAllowRunWithoutForecast(false);
		setHideInfoAlert(false);
	}, [checked, open]);

	const canSelectForecast =
		!!checked && !isLoadingForecast && areDatesValid && !allowRunWithoutForecast && hasAnyOptions;

	const canExecute =
		!isLoadingForecast &&
		!!checked &&
		areDatesValid &&
		!!selectedFile &&
		(!requiresForecast || !!selectedForecast || allowRunWithoutForecast) &&
		formValues.name.trim() !== "" &&
		formValues.description.trim() !== "";

	const handleButtonClick = async () => {
		const errors = {
			name: formValues.name.trim() === "",
			description: formValues.description.trim() === "",
		};
		setFormErrors(errors);

		if (requiresForecast && !selectedForecast && !allowRunWithoutForecast) {
			if (hasAnyOptions) setShowForecastError(true); // sólo pedimos selección si hay opciones
			return;
		} else {
			setShowForecastError(false);
		}

		if (!(fechaInicio && fechaFin)) {
			setFechaError(t("aux.messages.date_obligatory"));
			return;
		}
		if (!areDatesValid) {
			setFechaError(t("aux.messages.date_error"));
			return;
		}
		setFechaError("");

		if (!checked) {
			setCheckedError(t("aux.messages.select_checkbox"));
			return;
		} else {
			setCheckedError("");
		}

		if (errors.name || errors.description) {
			setShowAlert(true);
			return;
		} else {
			setShowAlert(false);
		}

		const data = {
			site_id: selectedIdArea,
			name: formValues.name,
			description: formValues.description,
			start: fechaInicio,
			end: fechaFin,
			send_mail: emailNotifications,
			forecast_origin: checked,
			...(allowRunWithoutForecast && allowRunWithoutForecast.fromOrigin
				? {}
				: { forecast_id: selectedForecast }),
		};

		onClose();

		if (selectedFile && data) {
			const result = await Swal.fire({
				title: t("aux.messages.sure"),
				text: t("aux.messages.sure_file"),
				icon: "question",
				showCancelButton: true,
				confirmButtonColor: "#3085d6",
				cancelButtonColor: "#d33",
				confirmButtonText: t("aux.buttons.yes"),
			});

			if (result.isConfirmed) {
				try {
					uploadFileSimulation(`/launch_w_params`, data, selectedFile);
					const moduleId = getModuleId("Simulación", "Ejecutar desde archivo");
					await auditPost(moduleId, selectedIdArea);
					Swal.fire({
						title: t("aux.messages.alert.success"),
						text: t("aux.messages.alert.text_alert.send_file"),
						icon: "success",
					});
				} catch (error) {
					Swal.fire({
						title: t("aux.messages.alert.error"),
						text: t("aux.messages.alert.text_alert.error_send_file"),
						icon: "error",
					});
					console.error("Error al enviar el archivo:", error);
				}
			}
		}
		handleCloseAndReset();
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0] ?? null;
		setSelectedFile(file);
	};

	const handleRadioChange = (event) => {
		const { name } = event.target;
		setChecked(name);
	};
	const handleCloseAndReset = (e) => {
		setHideInfoAlert(true);
		setChecked("");
		setFechaError("");
		setFechaFin("");
		setFechaInicio("");
		setFormValues({ name: "", description: "" });
		setFormErrors({ name: false, description: false });
		setShowAlert(false);
		setSelectedForecast("");
		setForecastOptions([]);
		setAllowRunWithoutForecast(false);
		onClose?.(e);
	};
	return (
		<Modal
			open={open}
			onClose={handleCloseAndReset}
			sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: "60%",
					backgroundColor: "background.paper",
					border: "1px",
					borderRadius: "4px",
					borderColor: "secondary.main",
					boxShadow: 30,
					p: 2,
					overflowY: "auto",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexDirection: "column",
				}}
			>
				<Typography variant="h6" gutterBottom>
					{t("aux.final_adjustments")}
				</Typography>

				<Grid container spacing={2} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
					<Grid item xs={12}>
						<FormControl
							required
							error={!!checkedError}
							component="fieldset"
							sx={{
								width: "100%",
								mb: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							variant="standard"
						>
							<FormGroup>
								<FormLabel component="legend">{t("sim.sim_form.origen_demand")}</FormLabel>
								<FormControlLabel
									control={
										<Radio checked={checked === "KRONOS"} onChange={handleRadioChange} name="KRONOS" />
									}
									label="UKG"
								/>
								<FormControlLabel
									control={<Radio checked={checked === "DB"} onChange={handleRadioChange} name="DB" />}
									label={t("sim.sim_form.db")}
								/>
							</FormGroup>
							{checkedError && <FormHelperText>{t("aux.messages.select_checkbox")}</FormHelperText>}
						</FormControl>
					</Grid>

					<Grid item xs={6}>
						<TextField
							id="dateInicio"
							label={t("aux.date_start")}
							type="month"
							value={fechaInicio}
							onChange={(e) => setFechaInicio(e.target.value)}
							InputLabelProps={{ shrink: true }}
							style={{ width: "100%", m: 2 }}
							InputProps={{ onKeyDown: (e) => e.preventDefault() }}
							error={!!fechaError}
							helperText={fechaError && !fechaInicio ? fechaError : ""}
							disabled={!canEditDates}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="dateFin"
							label={t("aux.date_end")}
							type="month"
							value={fechaFin}
							onChange={(e) => setFechaFin(e.target.value)}
							error={!!fechaError}
							helperText={
								fechaError
									? fechaError
									: fechaInicio && fechaFin && !areDatesValid
									? t("aux.messages.date_error")
									: ""
							}
							InputLabelProps={{ shrink: true }}
							style={{ width: "100%" }}
							InputProps={{ onKeyDown: (e) => e.preventDefault() }}
							disabled={!canEditDates}
						/>
					</Grid>

					{/* Select de forecasts (se oculta si el usuario decide ejecutar desde el origen) */}
					{(checked === "DB" || checked === "KRONOS") && !allowRunWithoutForecast && (
						<Grid item xs={12}>
							<FormControl
								required
								fullWidth
								error={showForecastError && !noFilesForOrigin}
								sx={{ mt: 1 }}
							>
								<FormLabel>{t("sim.sim_form.select_forecast")}</FormLabel>
								<Select
									value={selectedForecast}
									onChange={(e) => setSelectedForecast(e.target.value)}
									displayEmpty
									disabled={!canSelectForecast}
								>
									{isLoadingForecast ? (
										<MenuItem disabled>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<CircularProgress size={18} />
												<span>{t("aux.messages.loading")}</span>
											</Box>
										</MenuItem>
									) : filteredOptions.length > 0 ? (
										filteredOptions.map((opt) => (
											<MenuItem key={`${opt.value}`} value={opt.value}>
												{`${opt.label}${
													opt.displayStart && opt.displayEnd
														? ` (${opt.displayStart} \u2192 ${opt.displayEnd})`
														: ""
												}`}
											</MenuItem>
										))
									) : (
										<MenuItem disabled>— {t("aux.messages.no_forecasts_in_range")} —</MenuItem>
									)}
								</Select>

								{noFilesForOrigin && (
									<Box sx={{ mt: 1 }}>
										<Alert severity="warning" variant="outlined">
											{t("aux.messages.no_files")}
										</Alert>
									</Box>
								)}

								{showForecastError && !noFilesForOrigin && (
									<FormHelperText>{t("aux.messages.select_forecast_error")}</FormHelperText>
								)}
							</FormControl>
						</Grid>
					)}

					{shouldShowInfoAlert && infoMessageKey && !allowRunWithoutForecast && !hideInfoAlert && (
						<Grid item xs={12}>
							<Alert
								severity="info"
								variant="outlined"
								sx={(theme) => ({
									borderRadius: 3,
									borderWidth: 2,
									borderColor: theme.palette.info.main,
									bgcolor: theme.palette.mode === "dark" ? "transparent" : `${theme.palette.info.main}14`,
									"& .MuiAlert-icon": { color: theme.palette.info.main },
								})}
							>
								<AlertTitle>{t("aux.messages.alert.info")}</AlertTitle>
								<Typography sx={{ color: "text.secondary" }}>
									{t(infoMessageKey, { origin: originReadable })}
								</Typography>

								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: 2 }}>
									<Button
										variant="text"
										onClick={() => {
											setAllowRunWithoutForecast({ fromOrigin: true });
											setHideInfoAlert(true);
										}}
										sx={{ fontWeight: 700, textTransform: "none", color: "info.main" }}
									>
										{t("aux.buttons.run_from_origin", { origin: originReadable })}
									</Button>
									<Button
										variant="text"
										onClick={handleCloseAndReset}
										sx={{ fontWeight: 700, textTransform: "none", color: "text.primary" }}
									>
										{t("aux.buttons.go_back")}
									</Button>
								</Box>
							</Alert>
						</Grid>
					)}

					<Grid item xs={12}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
							<Button variant="outlined" component="label" sx={{ textTransform: "none" }}>
								{t("file.select")} {/* ← i18n */}
								<input
									hidden
									type="file"
									onChange={handleFileChange}
									// accept="application/pdf,.csv"  // opcional
									// multiple                     // opcional
								/>
							</Button>

							<Typography
								variant="body1"
								sx={{ color: "text.secondary", minWidth: 200, flex: 1 }}
								noWrap
								title={selectedFile ? selectedFile.name : undefined}
							>
								{selectedFile ? selectedFile.name : t("file.none_selected")} {/* ← i18n */}
							</Typography>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<FormNameDesc
							formValues={formValues}
							formErrors={formErrors}
							onFormChange={handleFormChange}
						/>
					</Grid>

					{showAlert && (
						<Grid item xs={12}>
							<Alert severity="error">{t("aux.messages.error_campos")}</Alert>
						</Grid>
					)}

					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={emailNotifications}
									onChange={(e) => setEmailNotifications(e.target.checked)}
								/>
							}
							label={t("sim.sim_form.email_notify")}
						/>
					</Grid>

					<Grid item xs={12}>
						<Button
							variant="contained"
							color="primary"
							onClick={handleButtonClick}
							sx={{ width: "100%" }}
							disabled={!canExecute}
						>
							{t("aux.buttons.sim.execute")}
						</Button>
					</Grid>
				</Grid>
			</Box>
		</Modal>
	);
};

export { ModalFechaUpload };
